import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link, useNavigate, useSearchParams } from 'react-router-dom';
import { 
  Utensils, 
  MapPin, 
  Phone, 
  Clock, 
  Star,
  Plus,
  Minus,
  ShoppingCart,
  X,
  Truck,
  ShoppingBag,
  Send,
  MessageSquare,
  Sparkles,
  Loader2,
  CheckCircle,
  Calendar,
  Users,
  Globe,
  AlertCircle,
  CreditCard,
  Euro,
  Check,
  Search,
  LayoutGrid,
  List,
  Trash2,
  ChevronRight,
  ChevronUp,
  ChefHat
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../components/ui/sheet';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { restaurantAPI, menuAPI, orderAPI, paymentAPI, reservationAPI, aiAPI, seedAPI, deliveryZoneAPI, publicAPI, marketingAPI, api } from '../lib/api';
import { useCart } from '../context/CartContext';
import { useLanguage } from '../context/LanguageContext';
import { formatPrice, generateSessionId, getLocalizedName, getLocalizedDescription } from '../lib/utils';
import { toast } from 'sonner';

// Check if restaurant is currently open based on opening_hours config
const getRestaurantOpenStatus = (openingHours) => {
  if (!openingHours) return { open: true, label: null };
  const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
  const now = new Date();
  const today = openingHours[dayNames[now.getDay()]];
  if (!today || today.closed) return { open: false, label: 'Gesloten vandaag' };
  const nowMin = now.getHours() * 60 + now.getMinutes();
  const parse = (t) => {
    const [h, m] = (t || '00:00').split(':').map(Number);
    return h * 60 + m;
  };
  const openMin = parse(today.open);
  let closeMin = parse(today.close);
  if (closeMin <= openMin) closeMin += 24 * 60; // after-midnight
  const nowEff = nowMin < openMin ? nowMin + 24 * 60 : nowMin;
  const isOpen = nowEff >= openMin && nowEff <= closeMin;
  return {
    open: isOpen,
    label: isOpen ? `Open tot ${today.close}` : `Gesloten · opent om ${today.open}`,
  };
};

// Open / Closed badge for restaurant based on opening_hours
const OpenStatusBadge = ({ openingHours }) => {
  const { open, label } = getRestaurantOpenStatus(openingHours);
  if (!label) return null;
  return (
    <span
      className={`flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${
        open ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'
      }`}
      data-testid="open-status-badge"
    >
      <span className={`w-1.5 h-1.5 rounded-full ${open ? 'bg-emerald-500' : 'bg-red-500'}`} />
      {label}
    </span>
  );
};

// Popover with full weekly opening hours schedule
const OpeningHoursPopover = ({ openingHours }) => {
  const [show, setShow] = useState(false);
  if (!openingHours) return null;
  const days = [
    { key: 'monday', label: 'Ma' },
    { key: 'tuesday', label: 'Di' },
    { key: 'wednesday', label: 'Wo' },
    { key: 'thursday', label: 'Do' },
    { key: 'friday', label: 'Vr' },
    { key: 'saturday', label: 'Za' },
    { key: 'sunday', label: 'Zo' },
  ];
  return (
    <div className="relative" data-testid="opening-hours-popover-wrapper">
      <button
        type="button"
        onClick={() => setShow((s) => !s)}
        className="text-xs text-gray-500 underline hover:text-gray-700"
        data-testid="opening-hours-popover-toggle"
      >
        Alle uren
      </button>
      {show && (
        <div className="absolute top-full left-0 mt-2 bg-white border border-gray-200 rounded-lg shadow-lg p-3 z-50 min-w-[200px]" data-testid="opening-hours-popover">
          <p className="text-xs font-semibold text-gray-700 mb-2">Openingsuren</p>
          <ul className="space-y-1">
            {days.map(({ key, label }) => {
              const d = openingHours[key];
              return (
                <li key={key} className="flex justify-between text-xs">
                  <span className="font-medium text-gray-700">{label}</span>
                  <span className={d?.closed ? 'text-red-500' : 'text-gray-600'}>
                    {d?.closed ? 'Gesloten' : `${d?.open || '-'} - ${d?.close || '-'}`}
                  </span>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
};

// Announcement Banner Component
const AnnouncementBanner = ({ announcement, onApplyCode }) => {
  if (!announcement?.active) return null;
  
  const discountText = announcement.discount_type === 'percentage' 
    ? `${announcement.discount_value}%` 
    : `€${announcement.discount_value.toFixed(2)}`;
  
  return (
    <div className="bg-gradient-to-r from-emerald-500 to-emerald-600 text-white px-4 py-2.5 text-center relative">
      <p className="text-sm">
        <Sparkles className="w-4 h-4 inline mr-1" />
        <strong>{discountText} KORTING</strong> {announcement.first_order_only ? 'op je eerste bestelling' : ''}! 
        {' '}Gebruik code: 
        <button 
          onClick={() => onApplyCode(announcement.code)}
          className="font-mono bg-white/20 px-2 py-0.5 rounded ml-1 hover:bg-white/30 transition-colors"
        >
          {announcement.code}
        </button>
      </p>
    </div>
  );
};

export const CustomerWebshop = ({ slugOverride }) => {
  const { slug: slugParam } = useParams();
  const slug = slugOverride || slugParam;
  const [restaurant, setRestaurant] = useState(null);
  const [categories, setCategories] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [cartOpen, setCartOpen] = useState(false);
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const [reservationOpen, setReservationOpen] = useState(false);
  const { items: cartItems, addItem, removeItem, updateQuantity, clearCart, getSubtotal, getItemCount, restaurantId } = useCart();
  const { language, t, switchLanguage, availableLanguages, languageNames } = useLanguage();
  const [announcement, setAnnouncement] = useState(null);
  const [appliedDiscount, setAppliedDiscount] = useState(null);

  // Menu UX enhancements: search, view-mode, section refs for smooth scroll
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState(() => {
    try { return localStorage.getItem('foodflows_shop_view') || 'grid'; } catch { return 'grid'; }
  });
  const [activeCategoryId, setActiveCategoryId] = useState(null);
  const categoryRefs = useRef({});
  const catTabsRef = useRef(null);

  const setViewModeAndPersist = (mode) => {
    setViewMode(mode);
    try { localStorage.setItem('foodflows_shop_view', mode); } catch { /* ignore */ }
  };

  // Scroll a category-tab into view when it becomes active (horizontal nav follows scroll)
  useEffect(() => {
    if (!activeCategoryId || !catTabsRef.current) return;
    const el = catTabsRef.current.querySelector(`[data-cat-tab="${activeCategoryId}"]`);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
    }
  }, [activeCategoryId]);

  // Set browser tab title & favicon based on restaurant branding
  useEffect(() => {
    if (!restaurant) return;
    // Title
    document.title = `${restaurant.name} — Bestellen`;
    // Favicon: prefer favicon_url, fallback to logo_url
    const iconUrl = restaurant.favicon_url || restaurant.logo_url;
    if (!iconUrl) return;
    let link = document.querySelector("link[rel~='icon']");
    if (!link) {
      link = document.createElement('link');
      link.rel = 'icon';
      document.head.appendChild(link);
    }
    link.href = iconUrl;
    // Apple touch icon (for iOS bookmarks)
    let apple = document.querySelector("link[rel='apple-touch-icon']");
    if (!apple) {
      apple = document.createElement('link');
      apple.rel = 'apple-touch-icon';
      document.head.appendChild(apple);
    }
    apple.href = iconUrl;
  }, [restaurant]);

  // IntersectionObserver: highlight the category whose section is currently visible
  useEffect(() => {
    if (categories.length === 0) return;
    const observer = new IntersectionObserver(
      (entries) => {
        // Pick the first entry that's currently intersecting near the top
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible[0]) {
          const cid = visible[0].target.dataset.categoryId;
          if (cid) setActiveCategoryId(cid);
        }
      },
      { rootMargin: '-140px 0px -60% 0px', threshold: 0 }
    );
    categories.forEach((cat) => {
      const el = categoryRefs.current[cat.id];
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, [categories, items, searchQuery]);

  const scrollToCategory = (categoryId) => {
    const el = categoryRefs.current[categoryId];
    if (!el) return;
    // Scroll with an offset for the sticky header + tabs
    const y = el.getBoundingClientRect().top + window.scrollY - 130;
    window.scrollTo({ top: y, behavior: 'smooth' });
    setActiveCategoryId(categoryId);
    setCatDropdownOpen(false);
  };

  // Floating category dropdown: visible once user scrolled past hero
  const [catDropdownOpen, setCatDropdownOpen] = useState(false);
  const [showFloatingCatBtn, setShowFloatingCatBtn] = useState(false);
  useEffect(() => {
    const onScroll = () => {
      setShowFloatingCatBtn(window.scrollY > 400);
      if (window.scrollY < 400) setCatDropdownOpen(false);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Fetch announcement
  useEffect(() => {
    const fetchAnnouncement = async () => {
      try {
        const response = await publicAPI.getAnnouncement(slug);
        if (response.data?.active) {
          setAnnouncement(response.data);
        }
      } catch (error) {
        console.log('No announcement available');
      }
    };
    if (slug) fetchAnnouncement();
  }, [slug]);

  const handleApplyAnnouncementCode = (code) => {
    // Copy to clipboard and show toast
    navigator.clipboard.writeText(code);
    toast.success(`Code "${code}" gekopieerd! Gebruik deze bij het afrekenen.`);
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        try {
          await seedAPI.seedDemoData();
        } catch (e) { /* seed already exists — safe to ignore */ }
        
        const restaurantRes = await restaurantAPI.getBySlug(slug);
        setRestaurant(restaurantRes.data);
        
        // Set language based on restaurant default
        if (restaurantRes.data.default_language) {
          switchLanguage(restaurantRes.data.default_language);
        }
        
        const [categoriesRes, itemsRes] = await Promise.all([
          menuAPI.getPublicCategories(restaurantRes.data.id),
          menuAPI.getPublicItems(restaurantRes.data.id),
        ]);
        setCategories(categoriesRes.data);
        setItems(itemsRes.data);
        
        if (categoriesRes.data.length > 0) {
          setSelectedCategory(categoriesRes.data[0].id);
        }
      } catch (error) {
        console.error('Error fetching restaurant:', error);
        toast.error(t('restaurant_not_found') || 'Restaurant niet gevonden');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [slug]);

  const [modifierContext, setModifierContext] = useState(null); // { item, groups }

  const handleAddToCart = async (item) => {
    if (!restaurant) return;
    try {
      // Check if this item has any modifier groups linked (either directly or via category)
      const res = await menuAPI.getItemModifiers(item.id);
      const groups = res.data?.modifier_groups || [];
      if (groups.length > 0) {
        // Open modifier picker — modal handles selection + add to cart
        setModifierContext({ item, groups });
        return;
      }
    } catch { /* fall through to direct add */ }
    addItem(item, restaurant.id, restaurant.slug);
    toast.success(`${getLocalizedName(item, language)} ${t('add_to_cart') || 'toegevoegd'}`);
  };

  const handleConfirmModifiers = (selectedMods, totalUnitPrice) => {
    const { item } = modifierContext;
    addItem(
      { ...item, price: totalUnitPrice, modifiers: selectedMods, quantity: 1 },
      restaurant.id,
      restaurant.slug,
    );
    toast.success(`${getLocalizedName(item, language)} ${t('add_to_cart') || 'toegevoegd'}`);
    setModifierContext(null);
  };

  const filteredItems = items.filter(item => item.category_id === selectedCategory);
  const featuredItems = items.filter(item => item.is_featured).slice(0, 4);
  const currency = restaurant?.currency || 'EUR';

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8F9FA]">
        <div className="spinner"></div>
      </div>
    );
  }

  if (!restaurant) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8F9FA]">
        <div className="text-center">
          <h1 className="font-heading text-2xl font-bold text-gray-900 mb-4">Restaurant niet gevonden</h1>
          <Link to="/">
            <Button className="btn-primary">Terug naar home</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50 pb-24 md:pb-0" style={{fontFamily: 'Manrope, ui-sans-serif, system-ui, sans-serif'}} data-testid="customer-webshop">
      {/* Announcement Banner */}
      <AnnouncementBanner announcement={announcement} onApplyCode={handleApplyAnnouncementCode} />
      
      {/* Header */}
      <div className="relative h-64 md:h-80 overflow-hidden">
        <img 
          src={restaurant.cover_image_url || "https://images.pexels.com/photos/7133566/pexels-photo-7133566.jpeg"}
          alt={restaurant.name}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent"></div>
        
        {/* Language Selector */}
        <div className="absolute top-4 right-4">
          <Select value={language} onValueChange={switchLanguage}>
            <SelectTrigger className="w-32 bg-white/90 backdrop-blur-sm" data-testid="language-selector">
              <Globe className="w-4 h-4 mr-2" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {availableLanguages.map((lang) => (
                <SelectItem key={lang} value={lang}>{languageNames[lang]}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        
        <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8">
          <div className="max-w-6xl mx-auto">
            <div className="flex items-end gap-4">
              {restaurant.logo_url && (
                <img 
                  src={restaurant.logo_url} 
                  alt={restaurant.name}
                  className="w-20 h-20 rounded-xl object-cover border-4 border-white shadow-lg"
                />
              )}
              <div className="text-white">
                <h1 className="font-heading text-3xl md:text-5xl font-bold mb-2 tracking-tight" style={{fontFamily: 'Playfair Display, ui-serif, serif'}}>{restaurant.name}</h1>
                <p className="text-white/80">{restaurant.cuisine_type}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Restaurant Info Bar */}
      <div className="bg-white border-b border-gray-100 sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-6 py-4">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600">
              {restaurant.address && (
                <span className="flex items-center gap-1">
                  <MapPin className="w-4 h-4" />
                  {restaurant.address}
                </span>
              )}
              {restaurant.phone && (
                <span className="flex items-center gap-1">
                  <Phone className="w-4 h-4" />
                  {restaurant.phone}
                </span>
              )}
              {restaurant.vat_number && (
                <span className="flex items-center gap-1 text-xs text-gray-500" data-testid="restaurant-vat">
                  BTW: {restaurant.vat_number}
                </span>
              )}
              <span className="flex items-center gap-1">
                <Clock className="w-4 h-4" />
                {restaurant.estimated_delivery_time} min {t('delivery')}
              </span>
              <OpenStatusBadge openingHours={restaurant.opening_hours} />
              <OpeningHoursPopover openingHours={restaurant.opening_hours} />
            </div>
            <div className="flex items-center gap-2">
              {restaurant.delivery_enabled && (
                <span className="flex items-center gap-1 px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm">
                  <Truck className="w-4 h-4" />
                  {t('delivery')}
                </span>
              )}
              {restaurant.pickup_enabled && (
                <span className="flex items-center gap-1 px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm">
                  <ShoppingBag className="w-4 h-4" />
                  {t('pickup')}
                </span>
              )}
              {/* Hing eerst aan dine_in_enabled, het verkeerde veld: wie
                  reserveringen uitzette maar ter plaatse eten aan liet, hield
                  de knop. Een ontbrekend veld telt als aan, gelijk aan wat het
                  instellingenscherm laat zien bij oudere restaurants. */}
              {restaurant.reservations_enabled !== false && (
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => setReservationOpen(true)}
                  data-testid="reserve-table-btn"
                >
                  <Calendar className="w-4 h-4 mr-1" />
                  {t('reserve_table')}
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-6xl mx-auto px-6 py-8">
        {/* Featured Items */}
        {featuredItems.length > 0 && (
          <div className="mb-12">
            <h2 className="font-heading text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2">
              <Star className="w-6 h-6 text-[#E07A5F]" />
              {t('featured')}
            </h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {featuredItems.map((item) => (
                <MenuItemCard 
                  key={item.id} 
                  item={item} 
                  onAdd={() => handleAddToCart(item)}
                  inCart={cartItems.find(i => i.id === item.id)?.quantity || 0}
                  language={language}
                  currency={currency}
                  t={t}
                />
              ))}
            </div>
          </div>
        )}

        {/* Search + View toggle */}
        {items.length > 0 && (
          <div className="mb-6 flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
              <Input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={t('search_menu') || 'Zoek in menu...'}
                className="pl-10 pr-10 h-11 bg-white shadow-sm border-gray-200"
                data-testid="menu-search-input"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  data-testid="menu-search-clear"
                  aria-label="Clear search"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
            <div className="flex items-center gap-1 bg-white rounded-lg border border-gray-200 p-1 shrink-0">
              <button
                onClick={() => setViewModeAndPersist('grid')}
                className={`px-3 py-2 rounded-md flex items-center gap-1.5 text-sm font-medium transition-colors ${
                  viewMode === 'grid' ? 'bg-[#1A4D2E] text-white' : 'text-gray-500 hover:text-gray-800'
                }`}
                data-testid="menu-view-grid"
                aria-label="Grid view"
              >
                <LayoutGrid className="w-4 h-4" />
                <span className="hidden sm:inline">Kaarten</span>
              </button>
              <button
                onClick={() => setViewModeAndPersist('list')}
                className={`px-3 py-2 rounded-md flex items-center gap-1.5 text-sm font-medium transition-colors ${
                  viewMode === 'list' ? 'bg-[#1A4D2E] text-white' : 'text-gray-500 hover:text-gray-800'
                }`}
                data-testid="menu-view-list"
                aria-label="List view"
              >
                <List className="w-4 h-4" />
                <span className="hidden sm:inline">Lijst</span>
              </button>
            </div>
          </div>
        )}

        {/* Sticky category tabs — horizontal scroll on mobile, highlights currently-viewed section */}
        {categories.length > 0 && !searchQuery && (
          <div
            className="sticky top-[73px] z-30 -mx-6 px-6 py-3 bg-[#F8F9FA]/95 backdrop-blur border-b border-gray-200"
            data-testid="menu-category-tabs"
          >
            <div ref={catTabsRef} className="flex gap-2 overflow-x-auto scrollbar-hide -mx-1 px-1">
              {categories.map((category) => (
                <button
                  key={category.id}
                  data-cat-tab={category.id}
                  onClick={() => scrollToCategory(category.id)}
                  className={`px-3 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all shrink-0 flex items-center gap-2 ${
                    activeCategoryId === category.id
                      ? 'bg-[#1A4D2E] text-white shadow-md'
                      : 'bg-white text-gray-700 border border-gray-200 hover:border-[#1A4D2E]/40'
                  }`}
                  data-testid={`category-tab-${category.id}`}
                >
                  {category.image_url && (
                    <img src={category.image_url} alt="" className="w-5 h-5 rounded-full object-cover" />
                  )}
                  {getLocalizedName(category, language)}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Menu — one section per category (all visible, single-scroll) */}
        <div className="mt-6 space-y-10" data-testid="menu-sections">
          {(() => {
            const q = searchQuery.trim().toLowerCase();
            const matchesSearch = (it) => {
              if (!q) return true;
              const name = (getLocalizedName(it, language) || '').toLowerCase();
              const desc = (getLocalizedDescription(it, language) || '').toLowerCase();
              return name.includes(q) || desc.includes(q);
            };
            const visibleCategories = categories
              .map((cat) => ({
                cat,
                items: items.filter((i) => i.category_id === cat.id && matchesSearch(i)),
              }))
              .filter(({ items: catItems }) => !q || catItems.length > 0);

            if (q && visibleCategories.length === 0) {
              return (
                <div className="text-center py-16">
                  <Search className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500 text-lg">Geen resultaten voor &quot;{searchQuery}&quot;</p>
                  <button
                    onClick={() => setSearchQuery('')}
                    className="mt-3 text-[#1A4D2E] font-medium underline"
                  >
                    Wis zoekopdracht
                  </button>
                </div>
              );
            }

            return visibleCategories.map(({ cat, items: catItems }) => (
              <section
                key={cat.id}
                ref={(el) => { categoryRefs.current[cat.id] = el; }}
                data-category-id={cat.id}
                className="scroll-mt-32"
                data-testid={`menu-section-${cat.id}`}
              >
                <h2 className="font-heading text-2xl font-bold text-gray-900 mb-4 flex items-center gap-3">
                  {cat.image_url && (
                    <img
                      src={cat.image_url}
                      alt=""
                      className="w-10 h-10 rounded-lg object-cover shadow-sm"
                    />
                  )}
                  <span>{getLocalizedName(cat, language)}</span>
                  <span className="text-sm font-normal text-gray-400">({catItems.length})</span>
                </h2>
                {catItems.length === 0 ? (
                  <p className="text-gray-400 text-sm italic">Geen items in deze categorie</p>
                ) : (
                  <div className={viewMode === 'grid'
                    ? 'grid sm:grid-cols-2 lg:grid-cols-3 gap-4'
                    : 'space-y-3'}
                  >
                    {catItems.map((item) => (
                      <MenuItemCard
                        key={item.id}
                        item={item}
                        onAdd={() => handleAddToCart(item)}
                        inCart={cartItems.find((i) => i.id === item.id)?.quantity || 0}
                        horizontal={viewMode === 'list'}
                        language={language}
                        currency={currency}
                        t={t}
                      />
                    ))}
                  </div>
                )}
              </section>
            ));
          })()}
        </div>
      </div>

      {/* Cart Button */}
      {getItemCount() > 0 && (
        <div className="fixed bottom-24 right-6 z-50">
          <Button 
            className="btn-primary h-14 px-6 shadow-xl"
            onClick={() => setCartOpen(true)}
            data-testid="cart-button"
          >
            <ShoppingCart className="w-5 h-5 mr-2" />
            {getItemCount()} {t('items')} • {formatPrice(getSubtotal(), currency)}
          </Button>
        </div>
      )}

      {/* Floating Category Dropdown — always accessible when scrolled */}
      {showFloatingCatBtn && categories.length > 0 && !searchQuery && (
        <div className="fixed bottom-6 right-6 z-50" data-testid="floating-category-picker">
          {catDropdownOpen && (
            <>
              {/* Backdrop */}
              <div
                className="fixed inset-0 bg-black/20 -z-10"
                onClick={() => setCatDropdownOpen(false)}
              />
              {/* Dropdown panel */}
              <div className="absolute bottom-16 right-0 w-72 max-h-[70vh] overflow-y-auto bg-white rounded-2xl shadow-2xl border border-gray-200 py-2 animate-in fade-in slide-in-from-bottom-2" data-testid="floating-category-list">
                <div className="px-4 py-2 border-b border-gray-100 text-xs font-semibold uppercase tracking-wider text-gray-500">
                  Categorieën
                </div>
                {categories.map((cat) => (
                  <button
                    key={cat.id}
                    onClick={() => scrollToCategory(cat.id)}
                    className={`w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors ${
                      activeCategoryId === cat.id
                        ? 'bg-[#1A4D2E]/10 text-[#1A4D2E] font-semibold'
                        : 'hover:bg-gray-50 text-gray-800'
                    }`}
                    data-testid={`floating-cat-${cat.id}`}
                  >
                    {cat.image_url ? (
                      <img src={cat.image_url} alt="" className="w-8 h-8 rounded-lg object-cover shrink-0" />
                    ) : (
                      <div className="w-8 h-8 rounded-lg bg-gray-100 shrink-0 flex items-center justify-center text-gray-400">
                        <ChefHat className="w-4 h-4" />
                      </div>
                    )}
                    <span className="text-sm truncate">{getLocalizedName(cat, language)}</span>
                  </button>
                ))}
              </div>
            </>
          )}
          <button
            onClick={() => setCatDropdownOpen((v) => !v)}
            className="h-14 px-5 bg-[#1A4D2E] text-white rounded-full shadow-xl flex items-center gap-2 hover:bg-[#143d24] transition-colors font-medium"
            data-testid="floating-category-btn"
            aria-label="Categorieën"
          >
            <List className="w-5 h-5" />
            <span className="text-sm">Categorieën</span>
            <ChevronUp className={`w-4 h-4 transition-transform ${catDropdownOpen ? 'rotate-180' : ''}`} />
          </button>
        </div>
      )}

      {/* AI Chat Button */}
      <button
        className="fixed bottom-6 left-6 w-14 h-14 bg-[#E07A5F] text-white rounded-full shadow-xl flex items-center justify-center hover:bg-[#d4694f] transition-colors z-50"
        onClick={() => setChatOpen(true)}
        data-testid="ai-chat-button"
      >
        <MessageSquare className="w-6 h-6" />
      </button>

      {/* Cart Sheet */}
      <Sheet open={cartOpen} onOpenChange={setCartOpen}>
        <SheetContent className="w-full sm:max-w-lg">
          <SheetHeader>
            <SheetTitle>{t('cart')}</SheetTitle>
          </SheetHeader>
          <div className="mt-6 flex flex-col h-[calc(100vh-200px)]">
            {cartItems.length === 0 ? (
              <div className="flex-1 flex items-center justify-center">
                <div className="text-center text-gray-500">
                  <ShoppingCart className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p>Uw winkelwagen is leeg</p>
                </div>
              </div>
            ) : (
              <>
                <div className="flex-1 overflow-y-auto space-y-4">
                  {cartItems.map((item) => {
                    const lineId = item.cart_line_id || item.id;
                    return (
                      <div key={lineId} className="flex items-start gap-4 p-4 bg-gray-50 rounded-xl" data-testid={`cart-item-${lineId}`}>
                        {item.image_url && (
                          <img src={item.image_url} alt={item.name} className="w-16 h-16 rounded-lg object-cover flex-shrink-0" />
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-gray-900 truncate">{getLocalizedName(item, language)}</p>
                          {item.modifiers && item.modifiers.length > 0 && (
                            <p className="text-xs text-gray-500 truncate mt-0.5">
                              {item.modifiers.map(m => m.option_name).join(', ')}
                            </p>
                          )}
                          <p className="text-sm text-gray-500 mt-1">{formatPrice(item.price, currency)}</p>
                        </div>
                        <div className="flex flex-col items-end gap-2">
                          <div className="flex items-center gap-2">
                            <button
                              className="w-8 h-8 flex items-center justify-center bg-white rounded-lg border hover:bg-gray-100 transition-colors"
                              onClick={() => updateQuantity(lineId, item.quantity - 1)}
                              data-testid={`cart-item-decrease-${lineId}`}
                              aria-label="Aantal verminderen"
                            >
                              <Minus className="w-4 h-4" />
                            </button>
                            <span className="w-8 text-center font-medium" data-testid={`cart-item-qty-${lineId}`}>{item.quantity}</span>
                            <button
                              className="w-8 h-8 flex items-center justify-center bg-white rounded-lg border hover:bg-gray-100 transition-colors"
                              onClick={() => updateQuantity(lineId, item.quantity + 1)}
                              data-testid={`cart-item-increase-${lineId}`}
                              aria-label="Aantal verhogen"
                            >
                              <Plus className="w-4 h-4" />
                            </button>
                          </div>
                          <button
                            className="text-xs text-red-500 hover:text-red-700 flex items-center gap-1 transition-colors"
                            onClick={() => removeItem(lineId)}
                            data-testid={`cart-item-remove-${lineId}`}
                            aria-label="Verwijder uit winkelmand"
                          >
                            <Trash2 className="w-3 h-3" />
                            Verwijder
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div className="border-t pt-4 mt-4 space-y-4">
                  <div className="flex justify-between text-lg">
                    <span className="text-gray-600">{t('subtotal')}</span>
                    <span className="font-bold text-gray-900">{formatPrice(getSubtotal(), currency)}</span>
                  </div>
                  <Button 
                    className="w-full btn-primary h-12"
                    onClick={() => { setCartOpen(false); setCheckoutOpen(true); }}
                    data-testid="checkout-btn"
                  >
                    {t('checkout')}
                  </Button>
                </div>
              </>
            )}
          </div>
        </SheetContent>
      </Sheet>

      {/* Delivery-area footer — auto-generated from this restaurant's delivery
          zones (platform-managed, see _compute_auto_seo in the backend). Lists
          the towns served so the page carries the local search terms customers
          actually use ("pizza Wuustwezel"). Hidden when the restaurant has no
          delivery zones configured. */}
      {restaurant?.seo_footer_text && (
        <div className="pt-8 pb-2 px-4 text-center">
          <p className="text-sm text-gray-500 max-w-2xl mx-auto" data-testid="delivery-area-footer">
            {restaurant.seo_footer_text}
          </p>
        </div>
      )}

      {/* Powered by footer — small, unobtrusive, links back to the platform */}
      <div className="py-6 text-center">
        <a
          href="https://food-flows.com"
          target="_blank"
          rel="noopener noreferrer"
          className="text-xs text-gray-400 hover:text-gray-500 transition-colors"
          data-testid="powered-by-footer"
        >
          Powered by food-flows.com
        </a>
      </div>

      {/* Checkout Modal */}
      <CheckoutModal
        open={checkoutOpen}
        onClose={() => setCheckoutOpen(false)}
        restaurant={restaurant}
        cartItems={cartItems}
        subtotal={getSubtotal()}
        onSuccess={clearCart}
        language={language}
        t={t}
      />

      {/* AI Chat Modal */}
      <AIChatModal
        open={chatOpen}
        onClose={() => setChatOpen(false)}
        restaurantId={restaurant?.id}
        language={language}
      />

      {/* Reservation Modal */}
      <ReservationModal
        open={reservationOpen}
        onClose={() => setReservationOpen(false)}
        restaurantId={restaurant?.id}
        t={t}
      />

      {/* Modifier picker — opens when adding an item with add-ons */}
      <ModifierPickerModal
        context={modifierContext}
        onClose={() => setModifierContext(null)}
        onConfirm={handleConfirmModifiers}
        currency={restaurant?.currency || 'EUR'}
      />

      {/* Floating cart bar (mobile only) — always visible when cart has items.
          Makes the cart much clearer per user request. */}
      {cartItems.length > 0 && !cartOpen && !checkoutOpen && (
        <div className="fixed bottom-0 left-0 right-0 z-40 md:hidden pointer-events-none" data-testid="floating-cart-bar">
          <div className="p-3 pointer-events-auto">
            <button
              onClick={() => setCartOpen(true)}
              className="w-full flex items-center justify-between gap-3 bg-orange-600 hover:bg-orange-700 text-white rounded-2xl px-5 py-4 shadow-2xl shadow-orange-600/30 transition-all active:scale-[0.98]"
              data-testid="floating-cart-btn"
            >
              <div className="flex items-center gap-3">
                <div className="relative">
                  <ShoppingCart className="w-6 h-6" />
                  <span className="absolute -top-2 -right-2 bg-white text-orange-600 text-xs font-bold w-5 h-5 rounded-full flex items-center justify-center">
                    {getItemCount()}
                  </span>
                </div>
                <span className="font-semibold text-base">
                  {getItemCount()} {getItemCount() === 1 ? 'artikel' : 'artikelen'}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <span className="font-semibold text-base">{formatPrice(getSubtotal(), restaurant?.currency || 'EUR')}</span>
                <ChevronRight className="w-5 h-5" />
              </div>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const MenuItemCard = ({ item, onAdd, inCart, horizontal, language, currency, t }) => {
  const name = getLocalizedName(item, language);
  const description = getLocalizedDescription(item, language);

  if (horizontal) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 flex gap-4" data-testid={`menu-item-${item.id}`}>
        {item.image_url && (
          <img src={item.image_url} alt={name} loading="lazy" decoding="async" className="w-24 h-24 rounded-lg object-cover" />
        )}
        <div className="flex-1">
          <div className="flex items-start justify-between">
            <div>
              <h4 className="font-heading font-semibold text-gray-900">{name}</h4>
              <p className="text-sm text-gray-500 mt-1 line-clamp-2">{description}</p>
            </div>
            <span className="font-heading font-bold text-[#1A4D2E] whitespace-nowrap ml-4">
              {formatPrice(item.price, currency)}
            </span>
          </div>
          <div className="flex items-center justify-between mt-3">
            <div className="flex items-center gap-2 text-sm text-gray-500">
              {item.prep_time_minutes && (
                <span className="flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  {item.prep_time_minutes} min
                </span>
              )}
            </div>
            <Button 
              size="sm" 
              className={inCart > 0 ? 'btn-secondary' : 'btn-primary'}
              onClick={onAdd}
              data-testid={`add-to-cart-${item.id}`}
            >
              <Plus className="w-4 h-4 mr-1" />
              {inCart > 0 ? `${t('add_to_cart')} (${inCart})` : t('add_to_cart')}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden group" data-testid={`menu-item-${item.id}`}>
      {item.image_url && (
        <div className="relative h-40 overflow-hidden">
          <img src={item.image_url} alt={name} loading="lazy" decoding="async" className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
          {item.is_featured && (
            <span className="absolute top-2 left-2 bg-[#E07A5F] text-white text-xs px-2 py-1 rounded-full">
              {t('featured')}
            </span>
          )}
        </div>
      )}
      <div className="p-4">
        <h4 className="font-heading font-semibold text-gray-900">{name}</h4>
        <p className="text-sm text-gray-500 mt-1 line-clamp-2">{description}</p>
        <div className="flex items-center justify-between mt-3">
          <span className="font-heading font-bold text-[#1A4D2E]">{formatPrice(item.price, currency)}</span>
          <Button size="sm" className="btn-primary" onClick={onAdd}>
            <Plus className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  );
};

const CheckoutModal = ({ open, onClose, restaurant, cartItems, subtotal, onSuccess, language, t }) => {
  const { clearCart } = useCart();
  const [formData, setFormData] = useState({
    customer_name: '',
    customer_email: '',
    customer_phone: '',
    street: '',
    house_number: '',
    city: '',
    postal_code: '',
    additional_info: '',
    order_type: 'delivery',
    special_instructions: '',
    tip_amount: 0,
    // Scheduling
    scheduled_type: 'asap', // 'asap' or 'scheduled'
    scheduled_date: '', // 'YYYY-MM-DD'
    scheduled_time: '', // 'HH:MM'
    // Default to cash if it's enabled (most reliable). Only fall back to online
    // when cash is explicitly disabled AND online is actually configured.
    payment_method:
      restaurant?.cash_enabled !== false
        ? 'cash'
        : (restaurant?.online_payments_available ? 'online' : 'cash'),
    discount_code: '',
  });
  const [loading, setLoading] = useState(false);
  const [deliveryCheck, setDeliveryCheck] = useState(null);
  const [checkingDelivery, setCheckingDelivery] = useState(false);
  const [discountApplied, setDiscountApplied] = useState(null);
  const [validatingDiscount, setValidatingDiscount] = useState(false);
  const [returningCustomer, setReturningCustomer] = useState(false);
  // Zorgen dat de code uit de link één keer geprobeerd wordt, en dat de
  // automatische welkomstkorting niet bij elke toetsaanslag opnieuw vraagt.
  const linkDiscountTried = useRef(false);
  const autoDiscountTried = useRef(null);
  // Available slots for scheduling (fetched from backend based on order_type)
  const [slotsData, setSlotsData] = useState(null);
  const [loadingSlots, setLoadingSlots] = useState(false);

  // Fetch available slots whenever order_type changes (or on open)
  useEffect(() => {
    if (!open || !restaurant?.id) return;
    let cancelled = false;
    (async () => {
      setLoadingSlots(true);
      try {
        const res = await publicAPI.getAvailableSlots(restaurant.id, formData.order_type);
        if (cancelled) return;
        setSlotsData(res.data);
        // If restaurant is currently closed, force customer into scheduled mode
        if (res.data?.config && !res.data.config.restaurant_open_now) {
          setFormData((prev) => (prev.scheduled_type === 'asap' ? { ...prev, scheduled_type: 'scheduled' } : prev));
        }
      } catch (_e) {
        // Silent — checkout still works, just no scheduling picker
      } finally {
        if (!cancelled) setLoadingSlots(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, restaurant?.id, formData.order_type]);

  // When user switches TO scheduled but hasn't picked a date/time yet, preselect the first available slot
  useEffect(() => {
    if (formData.scheduled_type !== 'scheduled' || !slotsData?.days?.length) return;
    if (formData.scheduled_date && formData.scheduled_time) return;
    const firstDayWithSlots = slotsData.days.find((d) => d.slots?.length > 0);
    if (firstDayWithSlots) {
      setFormData((prev) => ({
        ...prev,
        scheduled_date: firstDayWithSlots.date,
        scheduled_time: firstDayWithSlots.slots[0],
      }));
    }
  }, [formData.scheduled_type, slotsData]);

  // Auto-fill customer details for returning customers based on their email.
  // Debounced 600ms so we only query after the user stops typing.
  useEffect(() => {
    const email = (formData.customer_email || '').trim().toLowerCase();
    if (!restaurant?.id || !email || !email.includes('@') || !email.split('@')[1]?.includes('.')) {
      setReturningCustomer(false);
      return undefined;
    }
    const timer = setTimeout(async () => {
      try {
        const res = await api.get('/public/customer-lookup', {
          params: { restaurant_id: restaurant.id, email }
        });
        if (!res.data?.found) return;
        setReturningCustomer(true);
        setFormData(prev => ({
          ...prev,
          customer_name: prev.customer_name || res.data.customer_name || '',
          customer_phone: prev.customer_phone || res.data.customer_phone || '',
          street: prev.street || res.data.delivery_address?.street || '',
          house_number: prev.house_number || res.data.delivery_address?.house_number || '',
          city: prev.city || res.data.delivery_address?.city || '',
          postal_code: prev.postal_code || res.data.delivery_address?.postal_code || '',
        }));
      } catch (_e) {
        // Silent — auto-fill is a nice-to-have, never blocking
      }
    }, 600);
    return () => clearTimeout(timer);
  }, [formData.customer_email, restaurant?.id]);

  const currency = restaurant?.currency || 'EUR';

  // Check delivery availability when postal code changes
  useEffect(() => {
    const checkDelivery = async () => {
      if (formData.order_type === 'delivery' && formData.postal_code.length >= 4) {
        setCheckingDelivery(true);
        try {
          const response = await deliveryZoneAPI.check(restaurant.id, formData.postal_code);
          setDeliveryCheck(response.data);
        } catch (error) {
          setDeliveryCheck({ available: false });
        } finally {
          setCheckingDelivery(false);
        }
      } else {
        setDeliveryCheck(null);
      }
    };
    
    const debounce = setTimeout(checkDelivery, 500);
    return () => clearTimeout(debounce);
  }, [formData.postal_code, formData.order_type, restaurant?.id]);

  const deliveryFee = formData.order_type === 'delivery' 
    ? (deliveryCheck?.delivery_fee || restaurant?.default_delivery_fee || 0)
    : 0;
  const minOrder = deliveryCheck?.min_order_amount || restaurant?.default_min_order_amount || 0;
  
  // Calculate discount
  const discountAmount = discountApplied 
    ? (discountApplied.discount_type === 'percentage' 
        ? (subtotal * discountApplied.discount_value / 100) 
        : discountApplied.discount_value)
    : 0;
  
  const total = subtotal + deliveryFee + formData.tip_amount - discountAmount;

  const validateDiscountCode = async () => {
    if (!formData.discount_code.trim()) return;

    setValidatingDiscount(true);
    try {
      const response = await marketingAPI.validateDiscountCode(
        formData.discount_code,
        restaurant.id,
        formData.customer_email,
        formData.customer_phone
      );
      if (response.data.valid) {
        setDiscountApplied(response.data);
        toast.success(`Korting toegepast: ${response.data.discount_type === 'percentage' ? response.data.discount_value + '%' : '€' + response.data.discount_value}`);
      } else {
        toast.error(response.data.error || 'Ongeldige kortingscode');
        setDiscountApplied(null);
      }
    } catch (error) {
      toast.error('Kon kortingscode niet valideren');
    } finally {
      setValidatingDiscount(false);
    }
  };

  // Code uit de QR-/campagnelink (?discount=CODE) automatisch invullen en
  // valideren zodra we het restaurant kennen. De klant hoeft dan niets over te
  // typen na het scannen van een flyer.
  useEffect(() => {
    if (!restaurant?.id || discountApplied || linkDiscountTried.current) return;
    const fromLink = new URLSearchParams(window.location.search).get('discount');
    if (!fromLink) return;
    linkDiscountTried.current = true;
    const code = fromLink.trim().toUpperCase();
    setFormData(prev => ({ ...prev, discount_code: code }));
    marketingAPI
      .validateDiscountCode(code, restaurant.id, formData.customer_email, formData.customer_phone)
      .then(({ data }) => {
        if (data.valid) {
          setDiscountApplied(data);
          toast.success(
            `Korting toegepast: ${data.discount_type === 'percentage' ? data.discount_value + '%' : '€' + data.discount_value}`
          );
        }
        // Ongeldig (bv. verlopen): stil laten. De code staat ingevuld, de klant
        // ziet de reden pas als hij zelf op Toepassen klikt.
      })
      .catch(() => {});
  }, [restaurant?.id, discountApplied, formData.customer_email, formData.customer_phone]);

  // Automatische eerste-bestelling-korting: zodra de klant genoeg
  // contactgegevens heeft ingevuld, vraagt de webshop of er een campagne is
  // waar hij als nieuwe klant recht op heeft. De backend bepaalt of hij echt
  // nieuw is bij DIT restaurant. Nooit een handmatig toegepaste code overschrijven.
  useEffect(() => {
    if (!restaurant?.id || discountApplied) return;
    const email = (formData.customer_email || '').trim();
    const phone = (formData.customer_phone || '').trim();
    const hasEmail = email.includes('@') && email.length > 4;
    const hasPhone = phone.replace(/\D/g, '').length >= 6;
    if (!hasEmail && !hasPhone) return;

    const key = `${email.toLowerCase()}|${phone}`;
    if (autoDiscountTried.current === key) return;

    const timer = setTimeout(() => {
      autoDiscountTried.current = key;
      marketingAPI
        .getAutoDiscount(restaurant.id, email, phone)
        .then(({ data }) => {
          if (!data.found) return;
          setDiscountApplied(data);
          setFormData(prev => ({ ...prev, discount_code: data.code }));
          toast.success(
            `Welkomstkorting toegepast: ${data.discount_type === 'percentage' ? data.discount_value + '%' : '€' + data.discount_value}`
          );
        })
        .catch(() => {});
    }, 700); // korte debounce zodat we niet bij elke toetsaanslag vragen
    return () => clearTimeout(timer);
  }, [restaurant?.id, discountApplied, formData.customer_email, formData.customer_phone]);

  const isAddressComplete = formData.order_type !== 'delivery' || (
    formData.street && formData.house_number && formData.city && formData.postal_code
  );

  const meetsMinOrder = formData.order_type !== 'delivery' || subtotal >= minOrder;

  const submittingRef = useRef(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submittingRef.current || loading) return;  // prevent double-submit
    submittingRef.current = true;
    
    if (formData.order_type === 'delivery') {
      if (!isAddressComplete) {
        submittingRef.current = false;
        toast.error(t('address_required'));
        return;
      }
      if (!meetsMinOrder) {
        submittingRef.current = false;
        toast.error(`${t('min_order')}: ${formatPrice(minOrder, currency)}`);
        return;
      }
      if (deliveryCheck && !deliveryCheck.available) {
        submittingRef.current = false;
        toast.error(t('delivery_not_available'));
        return;
      }
    }

    // Scheduling validation (client-side; backend re-validates)
    if (formData.scheduled_type === 'scheduled') {
      if (!formData.scheduled_date || !formData.scheduled_time) {
        submittingRef.current = false;
        toast.error('Kies een geldig tijdstip voor je bestelling.');
        return;
      }
    } else if (slotsData?.config && !slotsData.config.restaurant_open_now) {
      // ASAP but restaurant closed
      submittingRef.current = false;
      toast.error('Het restaurant is nu gesloten. Kies een tijdstip om je bestelling in te plannen.');
      return;
    }
    
    setLoading(true);
    
    try {
      const orderData = {
        restaurant_id: restaurant.id,
        customer_name: formData.customer_name,
        customer_email: formData.customer_email,
        customer_phone: formData.customer_phone,
        delivery_address: formData.order_type === 'delivery' ? {
          street: formData.street,
          house_number: formData.house_number,
          city: formData.city,
          postal_code: formData.postal_code,
          additional_info: formData.additional_info,
        } : null,
        order_type: formData.order_type,
        payment_method: formData.payment_method,
        items: cartItems.map(item => ({
          item_id: item.id,
          name: getLocalizedName(item, language),
          price: item.price,
          quantity: item.quantity,
          modifiers: item.modifiers || [],
        })),
        special_instructions: formData.special_instructions,
        tip_amount: formData.tip_amount,
        discount_code: discountApplied?.code || null,
        discount_amount: discountAmount,
        scheduled_type: formData.scheduled_type,
        scheduled_for: formData.scheduled_type === 'scheduled' && formData.scheduled_date && formData.scheduled_time
          ? `${formData.scheduled_date}T${formData.scheduled_time}:00`
          : null,
      };
      
      const orderRes = await orderAPI.create(orderData);
      const createdOrderId = orderRes.data.id;
      
      // If online payment, redirect to Stripe (with rollback if checkout fails)
      if (formData.payment_method === 'online') {
        try {
          const originUrl = window.location.origin;
          const checkoutRes = await paymentAPI.createCheckout(createdOrderId, originUrl);
          clearCart();
          window.location.href = checkoutRes.data.url;
        } catch (checkoutErr) {
          // Roll back the order so it doesn't orphan into Live Orders
          try {
            await api.post(`/orders/${createdOrderId}/customer-cancel`, null, {
              params: { restaurant_id: restaurant.id }
            });
          } catch (e) { /* ignore */ }
          throw checkoutErr;
        }
      } else {
        // Cash - show success directly
        clearCart();
        toast.success('Bestelling verzonden! Het restaurant gaat hem nu bekijken.');
        onClose();
        window.location.href = `/shop/${restaurant.slug}/order-success?order_id=${createdOrderId}&payment_method=${formData.payment_method}`;
      }
    } catch (error) {
      // Show the actual backend error so user knows what's wrong
      const detail = error.response?.data?.detail;
      const status = error.response?.status;
      let msg = 'Kon bestelling niet plaatsen. Probeer opnieuw.';
      if (status === 400 && detail) {
        msg = typeof detail === 'string' ? detail : 'Ongeldige bestelgegevens. Controleer adres en items.';
      } else if (status === 422) {
        msg = 'Ongeldige gegevens. Controleer alle velden.';
      } else if (detail && typeof detail === 'string') {
        msg = detail;
      } else if (formData.payment_method === 'online') {
        msg = 'Online betaling is nu niet beschikbaar. Selecteer "Contant" en probeer opnieuw.';
      }
      console.error('[Checkout error]', { status, detail, error });
      toast.error(msg);
      setLoading(false);
      submittingRef.current = false;
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t('checkout')}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Order Type */}
          <div className="space-y-2">
            <Label>{t('order_type')}</Label>
            <RadioGroup
              value={formData.order_type}
              onValueChange={(value) => setFormData({ ...formData, order_type: value })}
              className="flex gap-4"
            >
              {restaurant?.delivery_enabled && (
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="delivery" id="delivery" />
                  <Label htmlFor="delivery" className="flex items-center gap-1 cursor-pointer">
                    <Truck className="w-4 h-4" /> {t('delivery')}
                  </Label>
                </div>
              )}
              {restaurant?.pickup_enabled && (
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="pickup" id="pickup" />
                  <Label htmlFor="pickup" className="flex items-center gap-1 cursor-pointer">
                    <ShoppingBag className="w-4 h-4" /> {t('pickup')}
                  </Label>
                </div>
              )}
            </RadioGroup>
          </div>

          {/* Scheduling — When would the customer like the order? */}
          {slotsData?.config?.enabled !== false && (
            <div className="space-y-3 p-4 bg-blue-50/60 border border-blue-100 rounded-xl" data-testid="scheduling-section">
              <Label className="flex items-center gap-2 text-blue-900 font-semibold">
                <Clock className="w-4 h-4" />
                Wanneer wil je de bestelling?
              </Label>

              {/* Closed banner */}
              {slotsData?.config && !slotsData.config.restaurant_open_now && (
                <div className="flex items-start gap-2 text-sm bg-amber-100 text-amber-900 p-3 rounded-lg" data-testid="restaurant-closed-banner">
                  <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                  <span>Het restaurant is nu <b>gesloten</b>. Je bestelling wordt verwerkt vanaf het gekozen tijdstip binnen de openingsuren.</span>
                </div>
              )}

              <RadioGroup
                value={formData.scheduled_type}
                onValueChange={(v) => setFormData({ ...formData, scheduled_type: v })}
                className="grid grid-cols-2 gap-2"
              >
                <label
                  htmlFor="sched-asap"
                  className={`flex items-center gap-2 p-3 rounded-lg border-2 cursor-pointer transition-all ${
                    formData.scheduled_type === 'asap'
                      ? 'border-[#1A4D2E] bg-white'
                      : 'border-transparent bg-white/70 hover:bg-white'
                  } ${slotsData?.config && !slotsData.config.restaurant_open_now ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  <RadioGroupItem
                    value="asap"
                    id="sched-asap"
                    disabled={!!(slotsData?.config && !slotsData.config.restaurant_open_now)}
                    data-testid="scheduling-asap-radio"
                  />
                  <div>
                    <p className="font-medium text-sm">Zo snel mogelijk</p>
                    <p className="text-xs text-gray-500">±{restaurant?.estimated_delivery_time || 30} min</p>
                  </div>
                </label>
                <label
                  htmlFor="sched-scheduled"
                  className={`flex items-center gap-2 p-3 rounded-lg border-2 cursor-pointer transition-all ${
                    formData.scheduled_type === 'scheduled'
                      ? 'border-[#1A4D2E] bg-white'
                      : 'border-transparent bg-white/70 hover:bg-white'
                  }`}
                >
                  <RadioGroupItem value="scheduled" id="sched-scheduled" data-testid="scheduling-scheduled-radio" />
                  <div>
                    <p className="font-medium text-sm">Kies een tijdstip</p>
                    <p className="text-xs text-gray-500">Plan vooruit</p>
                  </div>
                </label>
              </RadioGroup>

              {formData.scheduled_type === 'scheduled' && (
                <div className="grid grid-cols-2 gap-2" data-testid="scheduling-picker">
                  {loadingSlots && (
                    <div className="col-span-2 flex items-center gap-2 text-gray-500 text-sm">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Tijdsloten laden...
                    </div>
                  )}
                  {!loadingSlots && (
                    <>
                      <div className="space-y-1">
                        <Label className="text-xs text-gray-600">Dag</Label>
                        <Select
                          value={formData.scheduled_date}
                          onValueChange={(v) => {
                            const day = slotsData?.days?.find((d) => d.date === v);
                            setFormData({
                              ...formData,
                              scheduled_date: v,
                              scheduled_time: day?.slots?.[0] || '',
                            });
                          }}
                        >
                          <SelectTrigger data-testid="scheduling-date-select">
                            <SelectValue placeholder="Kies dag" />
                          </SelectTrigger>
                          <SelectContent>
                            {slotsData?.days?.map((d) => (
                              <SelectItem key={d.date} value={d.date} disabled={d.slots?.length === 0}>
                                {d.label} {d.slots?.length === 0 ? '(gesloten)' : ''}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs text-gray-600">Tijdstip</Label>
                        <Select
                          value={formData.scheduled_time}
                          onValueChange={(v) => setFormData({ ...formData, scheduled_time: v })}
                          disabled={!formData.scheduled_date}
                        >
                          <SelectTrigger data-testid="scheduling-time-select">
                            <SelectValue placeholder="Kies tijd" />
                          </SelectTrigger>
                          <SelectContent>
                            {(slotsData?.days?.find((d) => d.date === formData.scheduled_date)?.slots || []).map((s) => (
                              <SelectItem key={s} value={s}>{s}</SelectItem>
                            ))}
                            {(slotsData?.days?.find((d) => d.date === formData.scheduled_date)?.slots || []).length === 0 && (
                              <div className="px-2 py-1.5 text-xs text-gray-500">Geen slots beschikbaar</div>
                            )}
                          </SelectContent>
                        </Select>
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Customer Info */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2 col-span-2">
              <Label htmlFor="name">{t('name')} *</Label>
              <Input
                id="name"
                value={formData.customer_name}
                onChange={(e) => setFormData({ ...formData, customer_name: e.target.value })}
                required
                data-testid="checkout-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">{t('email')} *</Label>
              <Input
                id="email"
                type="email"
                required
                value={formData.customer_email}
                onChange={(e) => setFormData({ ...formData, customer_email: e.target.value })}
                data-testid="checkout-email-input"
              />
              {returningCustomer && (
                <p className="text-xs text-emerald-600 flex items-center gap-1 mt-1" data-testid="returning-customer-notice">
                  <CheckCircle className="w-3 h-3" />
                  Welkom terug! We hebben je gegevens automatisch ingevuld.
                </p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="phone">{t('phone')} *</Label>
              <Input
                id="phone"
                value={formData.customer_phone}
                onChange={(e) => setFormData({ ...formData, customer_phone: e.target.value })}
                required
                data-testid="checkout-phone-input"
              />
            </div>
          </div>

          {/* Delivery Address - Required fields */}
          {formData.order_type === 'delivery' && (
            <div className="space-y-4 p-4 bg-gray-50 rounded-xl">
              <h4 className="font-medium text-gray-900 flex items-center gap-2">
                <MapPin className="w-4 h-4" />
                {t('delivery')} - {t('address_required')}
              </h4>
              
              {/* Postal Code with check */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="postal_code">{t('postal_code')} *</Label>
                  <Input
                    id="postal_code"
                    value={formData.postal_code}
                    onChange={(e) => setFormData({ ...formData, postal_code: e.target.value })}
                    placeholder="1000"
                    required
                    data-testid="checkout-postal-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="city">{t('city')} *</Label>
                  <Input
                    id="city"
                    value={formData.city}
                    onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                    placeholder="Brussel"
                    required
                    data-testid="checkout-city-input"
                  />
                </div>
              </div>

              {/* Delivery check result */}
              {checkingDelivery && (
                <div className="flex items-center gap-2 text-gray-500 text-sm">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Controleren...
                </div>
              )}
              {deliveryCheck && !checkingDelivery && (
                <div className={`flex items-center gap-2 text-sm p-2 rounded-lg ${
                  deliveryCheck.available ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
                }`}>
                  {deliveryCheck.available ? (
                    <>
                      <CheckCircle className="w-4 h-4" />
                      {t('delivery_available')} - {t('delivery_fee')}: {formatPrice(deliveryCheck.delivery_fee, currency)}, {t('min_order')}: {formatPrice(deliveryCheck.min_order_amount, currency)}
                    </>
                  ) : (
                    <>
                      <AlertCircle className="w-4 h-4" />
                      {t('delivery_not_available')}
                    </>
                  )}
                </div>
              )}

              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2 col-span-2">
                  <Label htmlFor="street">{t('street')} *</Label>
                  <Input
                    id="street"
                    value={formData.street}
                    onChange={(e) => setFormData({ ...formData, street: e.target.value })}
                    placeholder="Grote Markt"
                    required
                    data-testid="checkout-street-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="house_number">{t('house_number')} *</Label>
                  <Input
                    id="house_number"
                    value={formData.house_number}
                    onChange={(e) => setFormData({ ...formData, house_number: e.target.value })}
                    placeholder="1"
                    required
                    data-testid="checkout-number-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="additional_info">{t('additional_info')}</Label>
                <Input
                  id="additional_info"
                  value={formData.additional_info}
                  onChange={(e) => setFormData({ ...formData, additional_info: e.target.value })}
                  placeholder="Appartement, verdieping, etc."
                  data-testid="checkout-additional-input"
                />
              </div>
            </div>
          )}

          {/* Special Instructions */}
          <div className="space-y-2">
            <Label htmlFor="instructions">{t('special_instructions')}</Label>
            <Textarea
              id="instructions"
              value={formData.special_instructions}
              onChange={(e) => setFormData({ ...formData, special_instructions: e.target.value })}
              rows={2}
              placeholder="Speciale verzoeken..."
              data-testid="checkout-instructions-input"
            />
          </div>

          {/* Tip */}
          <div className="space-y-2">
            <Label>{t('tip')}</Label>
            <div className="flex gap-2">
              {[0, 2, 5, 10].map((amount) => (
                <button
                  key={amount}
                  type="button"
                  className={`flex-1 py-2 rounded-lg border transition-colors ${
                    formData.tip_amount === amount
                      ? 'bg-[#1A4D2E] text-white border-[#1A4D2E]'
                      : 'border-gray-200 hover:border-[#1A4D2E]'
                  }`}
                  onClick={() => setFormData({ ...formData, tip_amount: amount })}
                >
                  {amount === 0 ? 'Geen' : `€${amount}`}
                </button>
              ))}
            </div>
          </div>

          {/* Discount Code */}
          <div className="space-y-2">
            <Label>Kortingscode</Label>
            <div className="flex gap-2">
              <Input
                value={formData.discount_code}
                onChange={(e) => setFormData({ ...formData, discount_code: e.target.value.toUpperCase() })}
                placeholder="Code invoeren"
                className="font-mono"
                data-testid="checkout-discount-input"
              />
              <Button
                type="button"
                variant="outline"
                onClick={validateDiscountCode}
                disabled={validatingDiscount || !formData.discount_code.trim()}
              >
                {validatingDiscount ? 'Controleren...' : 'Toepassen'}
              </Button>
            </div>
            {discountApplied && (
              <div className="flex items-center gap-2 text-sm text-emerald-600 bg-emerald-50 p-2 rounded-lg">
                <Check className="w-4 h-4" />
                {discountApplied.discount_type === 'percentage' 
                  ? `${discountApplied.discount_value}% korting toegepast!`
                  : `€${discountApplied.discount_value.toFixed(2)} korting toegepast!`}
              </div>
            )}
          </div>

          {/* Payment Method */}
          <div className="space-y-2">
            <Label>Betaalmethode</Label>
            <div className="grid grid-cols-1 gap-2">
              {/* Only show Online if it's both enabled by restaurant AND actually configured (Stripe ready) */}
              {restaurant?.online_payment_enabled !== false && restaurant?.online_payments_available && (
                <button
                  type="button"
                  className={`flex items-center gap-3 p-3 rounded-lg border-2 transition-all ${
                    formData.payment_method === 'online'
                      ? 'border-[#1A4D2E] bg-[#1A4D2E]/5'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                  onClick={() => setFormData({ ...formData, payment_method: 'online' })}
                  data-testid="payment-online-btn"
                >
                  <CreditCard className={`w-5 h-5 ${formData.payment_method === 'online' ? 'text-[#1A4D2E]' : 'text-gray-400'}`} />
                  <div className="text-left">
                    <p className={`font-medium ${formData.payment_method === 'online' ? 'text-[#1A4D2E]' : 'text-gray-700'}`}>
                      Online Betalen
                    </p>
                    <p className="text-xs text-gray-500">Veilig via iDEAL, Bancontact, Visa, etc.</p>
                  </div>
                </button>
              )}
              {restaurant?.cash_enabled !== false && (
                <button
                  type="button"
                  className={`flex items-center gap-3 p-3 rounded-lg border-2 transition-all ${
                    formData.payment_method === 'cash'
                      ? 'border-[#1A4D2E] bg-[#1A4D2E]/5'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                  onClick={() => setFormData({ ...formData, payment_method: 'cash' })}
                >
                  <Euro className={`w-5 h-5 ${formData.payment_method === 'cash' ? 'text-[#1A4D2E]' : 'text-gray-400'}`} />
                  <div className="text-left">
                    <p className={`font-medium ${formData.payment_method === 'cash' ? 'text-[#1A4D2E]' : 'text-gray-700'}`}>
                      Contant bij {formData.order_type === 'delivery' ? 'Bezorging' : 'Afhalen'}
                    </p>
                    <p className="text-xs text-gray-500">Betaal cash bij ontvangst</p>
                  </div>
                </button>
              )}
            </div>
          </div>

          {/* Order Summary */}
          <div className="border-t pt-4 space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-gray-500">{t('subtotal')}</span>
              <span>{formatPrice(subtotal, currency)}</span>
            </div>
            {deliveryFee > 0 && (
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">{t('delivery_fee')}</span>
                <span>{formatPrice(deliveryFee, currency)}</span>
              </div>
            )}
            {formData.tip_amount > 0 && (
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">{t('tip')}</span>
                <span>{formatPrice(formData.tip_amount, currency)}</span>
              </div>
            )}
            {discountAmount > 0 && (
              <div className="flex justify-between text-sm text-emerald-600">
                <span>Korting ({discountApplied?.code})</span>
                <span>-{formatPrice(discountAmount, currency)}</span>
              </div>
            )}
            <div className="flex justify-between font-semibold text-lg pt-2 border-t">
              <span>{t('total')}</span>
              <span className="text-[#1A4D2E]">{formatPrice(total, currency)}</span>
            </div>
            
            {/* Min order warning */}
            {formData.order_type === 'delivery' && !meetsMinOrder && (
              <div className="flex items-center gap-2 text-sm text-orange-600 bg-orange-50 p-2 rounded-lg">
                <AlertCircle className="w-4 h-4" />
                {t('min_order')}: {formatPrice(minOrder, currency)} (nog {formatPrice(minOrder - subtotal, currency)} nodig)
              </div>
            )}
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Annuleren</Button>
            <Button 
              type="submit" 
              className="btn-primary" 
              disabled={
                loading ||
                (formData.order_type === 'delivery' && (!deliveryCheck?.available || !meetsMinOrder || !isAddressComplete)) ||
                (formData.scheduled_type === 'scheduled' && (!formData.scheduled_date || !formData.scheduled_time)) ||
                (formData.scheduled_type === 'asap' && slotsData?.config && !slotsData.config.restaurant_open_now)
              } 
              data-testid="place-order-btn"
            >
              {loading ? 'Verwerken...' : `${t('place_order')} ${formatPrice(total, currency)}`}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const AIChatModal = ({ open, onClose, restaurantId, language }) => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [sessionId] = useState(() => generateSessionId());
  const chatEndRef = useRef(null);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const sendMessage = async () => {
    if (!input.trim()) return;
    
    const userMessage = { role: 'user', content: input };
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setSending(true);
    
    try {
      const response = await aiAPI.chat({
        message: input,
        session_id: sessionId,
        restaurant_id: restaurantId,
        context_type: 'customer',
        language: language
      });
      setMessages(prev => [...prev, { role: 'assistant', content: response.data.response }]);
    } catch (error) {
      setMessages(prev => [...prev, { role: 'assistant', content: 'Sorry, er is een fout opgetreden. Probeer het opnieuw.' }]);
    } finally {
      setSending(false);
    }
  };

  const placeholders = {
    nl: 'Vraag over het menu...',
    fr: 'Demandez à propos du menu...',
    en: 'Ask about the menu...'
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md h-[500px] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-[#E07A5F]" />
            Bestel Assistent
          </DialogTitle>
        </DialogHeader>
        
        <div className="flex-1 overflow-y-auto space-y-3 py-4">
          {messages.length === 0 && (
            <div className="text-center text-gray-500 py-8">
              <MessageSquare className="w-10 h-10 mx-auto mb-2 text-gray-300" />
              <p>Hallo! Hoe kan ik u helpen?</p>
              <p className="text-sm mt-1">Vraag over het menu, aanbevelingen, of iets anders!</p>
            </div>
          )}
          {messages.map((msg, index) => (
            <div key={index} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-[80%] ${msg.role === 'user' ? 'chat-user' : 'chat-assistant'}`}>
                {msg.content}
              </div>
            </div>
          ))}
          {sending && (
            <div className="flex justify-start">
              <div className="chat-assistant flex items-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin" />
                Denken...
              </div>
            </div>
          )}
          <div ref={chatEndRef} />
        </div>

        <div className="flex gap-2 pt-4 border-t">
          <Input
            placeholder={placeholders[language] || placeholders.nl}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
            disabled={sending}
            data-testid="customer-chat-input"
          />
          <Button className="btn-primary" onClick={sendMessage} disabled={sending || !input.trim()}>
            <Send className="w-4 h-4" />
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const ReservationModal = ({ open, onClose, restaurantId, t }) => {
  const [formData, setFormData] = useState({
    customer_name: '',
    customer_email: '',
    customer_phone: '',
    party_size: 2,
    reservation_date: '',
    reservation_time: '',
    special_requests: '',
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await reservationAPI.create({
        restaurant_id: restaurantId,
        ...formData,
      });
      setSuccess(true);
      toast.success('Reservering ingediend!');
    } catch (error) {
      toast.error('Kon reservering niet indienen');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <Dialog open={open} onOpenChange={() => { onClose(); setSuccess(false); }}>
        <DialogContent>
          <div className="text-center py-8">
            <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
            <h3 className="font-heading text-xl font-bold text-gray-900 mb-2">Reservering Ingediend!</h3>
            <p className="text-gray-500">We nemen contact met u op om uw boeking te bevestigen.</p>
            <Button className="btn-primary mt-6" onClick={() => { onClose(); setSuccess(false); }}>
              Sluiten
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Calendar className="w-5 h-5 text-[#1A4D2E]" />
            {t('reserve_table')}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2 col-span-2">
              <Label>{t('name')} *</Label>
              <Input
                value={formData.customer_name}
                onChange={(e) => setFormData({ ...formData, customer_name: e.target.value })}
                required
                data-testid="reservation-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label>{t('email')}</Label>
              <Input
                type="email"
                value={formData.customer_email}
                onChange={(e) => setFormData({ ...formData, customer_email: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>{t('phone')} *</Label>
              <Input
                value={formData.customer_phone}
                onChange={(e) => setFormData({ ...formData, customer_phone: e.target.value })}
                required
              />
            </div>
            <div className="space-y-2">
              <Label>{t('date')} *</Label>
              <Input
                type="date"
                value={formData.reservation_date}
                onChange={(e) => setFormData({ ...formData, reservation_date: e.target.value })}
                min={new Date().toISOString().split('T')[0]}
                required
              />
            </div>
            <div className="space-y-2">
              <Label>{t('time')} *</Label>
              <Input
                type="time"
                value={formData.reservation_time}
                onChange={(e) => setFormData({ ...formData, reservation_time: e.target.value })}
                required
              />
            </div>
            <div className="space-y-2 col-span-2">
              <Label>{t('party_size')} *</Label>
              <div className="flex items-center gap-4">
                <Users className="w-5 h-5 text-gray-400" />
                <Input
                  type="number"
                  min="1"
                  max="20"
                  value={formData.party_size}
                  onChange={(e) => setFormData({ ...formData, party_size: parseInt(e.target.value) })}
                  className="w-24"
                  required
                />
                <span className="text-gray-500">personen</span>
              </div>
            </div>
          </div>
          <div className="space-y-2">
            <Label>{t('special_requests')}</Label>
            <Textarea
              value={formData.special_requests}
              onChange={(e) => setFormData({ ...formData, special_requests: e.target.value })}
              placeholder="Speciale verzoeken of dieetwensen..."
              rows={2}
            />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Annuleren</Button>
            <Button type="submit" className="btn-primary" disabled={loading} data-testid="submit-reservation-btn">
              {loading ? 'Verzenden...' : t('submit')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// ============== MODIFIER PICKER MODAL ==============
// Shown when a customer adds an item that has add-on groups configured.
// Enforces `required` + `min_selections` and computes the live total price.
const ModifierPickerModal = ({ context, onClose, onConfirm, currency = 'EUR' }) => {
  const { item, groups } = context || { item: null, groups: [] };
  // selected: { [groupId]: Set<optionId> }
  const [selected, setSelected] = useState(() => {
    const init = {};
    (groups || []).forEach(g => {
      init[g.id] = new Set(
        (g.options || []).filter(o => o.is_default).map(o => o.id)
      );
    });
    return init;
  });

  if (!item) return null;

  const toggleOption = (group, optionId) => {
    setSelected(prev => {
      const next = { ...prev };
      const cur = new Set(next[group.id] || []);
      if (group.max_selections <= 1) {
        // Single-choice: replace
        cur.clear();
        cur.add(optionId);
      } else if (cur.has(optionId)) {
        cur.delete(optionId);
      } else if (cur.size < group.max_selections) {
        cur.add(optionId);
      } else {
        toast.warning(`Maximaal ${group.max_selections} keuzes voor ${group.name}`);
      }
      next[group.id] = cur;
      return next;
    });
  };

  const collectModifiers = () => {
    const result = [];
    let extra = 0;
    for (const g of groups) {
      const chosen = selected[g.id] || new Set();
      for (const opt of (g.options || [])) {
        if (chosen.has(opt.id)) {
          result.push({
            group_name: g.name,
            option_name: opt.name,
            price_delta: opt.price_delta || 0,
          });
          extra += opt.price_delta || 0;
        }
      }
    }
    return { modifiers: result, totalUnitPrice: (item.price || 0) + extra };
  };

  const validate = () => {
    for (const g of groups) {
      const count = (selected[g.id] || new Set()).size;
      const min = g.required ? Math.max(1, g.min_selections || 0) : (g.min_selections || 0);
      if (count < min) {
        toast.error(`Kies minstens ${min} optie voor "${g.name}"`);
        return false;
      }
    }
    return true;
  };

  const handleConfirm = () => {
    if (!validate()) return;
    const { modifiers, totalUnitPrice } = collectModifiers();
    onConfirm(modifiers, totalUnitPrice);
  };

  const { totalUnitPrice } = collectModifiers();

  return (
    <Dialog open={!!context} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto" data-testid="modifier-picker-modal">
        <DialogHeader>
          <DialogTitle className="text-xl">{item.name}</DialogTitle>
        </DialogHeader>

        <div className="space-y-5 py-2">
          {groups.map(g => {
            const chosen = selected[g.id] || new Set();
            const isSingle = g.max_selections <= 1;
            return (
              <div key={g.id} data-testid={`mod-group-${g.id}`}>
                <div className="flex items-center justify-between mb-2">
                  <h4 className="font-medium text-gray-900">
                    {g.name}
                    {g.required && (
                      <span className="ml-2 text-xs bg-rose-100 text-rose-700 px-2 py-0.5 rounded-full font-medium">
                        Verplicht
                      </span>
                    )}
                  </h4>
                  <span className="text-xs text-gray-400">
                    {isSingle ? 'Kies 1' : `Kies max ${g.max_selections}`}
                  </span>
                </div>
                {g.description && <p className="text-xs text-gray-500 mb-2">{g.description}</p>}
                <div className="space-y-1.5">
                  {(g.options || []).map(opt => {
                    const isChosen = chosen.has(opt.id);
                    return (
                      <label
                        key={opt.id}
                        className={`flex items-center justify-between p-3 rounded-xl border-2 cursor-pointer transition ${
                          isChosen ? 'border-emerald-500 bg-emerald-50' : 'border-gray-200 hover:border-gray-300'
                        }`}
                        data-testid={`mod-option-${opt.id}`}
                      >
                        <div className="flex items-center gap-2">
                          <input
                            type={isSingle ? 'radio' : 'checkbox'}
                            checked={isChosen}
                            onChange={() => toggleOption(g, opt.id)}
                            className="w-4 h-4 accent-emerald-500"
                          />
                          <span className="text-sm font-medium text-gray-800">{opt.name}</span>
                        </div>
                        {opt.price_delta > 0 && (
                          <span className="text-sm font-mono text-gray-600">
                            +{currency === 'EUR' ? '€' : ''}{Number(opt.price_delta).toFixed(2)}
                          </span>
                        )}
                      </label>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>

        <DialogFooter className="flex-row justify-between items-center gap-2 sm:gap-2">
          <Button variant="outline" onClick={onClose}>Annuleren</Button>
          <Button className="btn-primary flex-1" onClick={handleConfirm} data-testid="confirm-modifiers-btn">
            Voeg toe · {currency === 'EUR' ? '€' : ''}{totalUnitPrice.toFixed(2)}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};



export const OrderSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const orderIdParam = searchParams.get('order_id');
  const paymentMethod = searchParams.get('payment_method');

  // Stages of customer-facing order tracking:
  // - 'checking_payment': polling Stripe for payment confirmation (online orders)
  // - 'awaiting_confirmation': order is in restaurant queue (status=pending)
  // - 'confirmed': restaurant accepted (status=confirmed/preparing/ready) → show ready time
  // - 'rejected': restaurant cancelled (status=cancelled)
  // - 'payment_pending': Stripe still processing
  // - 'payment_failed': Stripe confirmed the payment failed/expired (order_status=payment_failed)
  // - 'payment_error': Stripe verification failed
  const [stage, setStage] = useState(
    orderIdParam && paymentMethod === 'cash' ? 'awaiting_confirmation' : 'checking_payment'
  );
  const [order, setOrder] = useState(null);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [resolvedOrderId, setResolvedOrderId] = useState(orderIdParam);

  // STEP 1: Resolve order_id from payment session (online) or use param (cash)
  useEffect(() => {
    if (resolvedOrderId) return undefined;
    let cancelled = false;
    let attempts = 0;
    const checkPayment = async () => {
      if (!sessionId) {
        setStage('payment_error');
        return;
      }
      try {
        const response = await paymentAPI.getStatus(sessionId);
        if (cancelled) return;
        setPaymentStatus(response.data);
        if (response.data.payment_status === 'paid') {
          setResolvedOrderId(response.data.order_id);
          setStage('awaiting_confirmation');
          return;
        }
        // Backend already determined this attempt definitively failed/expired —
        // no point retrying the remaining poll attempts, show it immediately.
        if (response.data.order_status === 'payment_failed') {
          setStage('payment_failed');
          return;
        }
        attempts++;
        if (attempts < 10) setTimeout(checkPayment, 3000);
        else setStage('payment_pending');
      } catch (e) {
        if (!cancelled) setStage('payment_error');
      }
    };
    // Defer to next microtask so state changes happen after render commit
    const t = setTimeout(checkPayment, 0);
    return () => { cancelled = true; clearTimeout(t); };
  }, [sessionId, resolvedOrderId]);

  // STEP 2: Once we have order_id, poll order status every 4s until confirmed/cancelled
  useEffect(() => {
    if (!resolvedOrderId) return;
    let cancelled = false;
    const fetchOrder = async () => {
      try {
        const res = await orderAPI.getById(resolvedOrderId);
        if (cancelled) return;
        const o = res.data;
        setOrder(o);
        if (o.status === 'cancelled') {
          setStage('rejected');
          return;
        }
        if (['confirmed', 'preparing', 'ready', 'out_for_delivery', 'delivered'].includes(o.status)) {
          setStage('confirmed');
          return;
        }
        // still pending — keep polling
      } catch (e) {
        // ignore transient errors and keep polling
      }
    };
    fetchOrder();
    const interval = setInterval(fetchOrder, 4000);
    return () => { cancelled = true; clearInterval(interval); };
  }, [resolvedOrderId]);

  const webshopBack = order?.restaurant_slug ? `/shop/${order.restaurant_slug}` : '/';
  const formatReadyTime = (iso) => {
    if (!iso) return '';
    try {
      return new Date(iso).toLocaleTimeString('nl-BE', { hour: '2-digit', minute: '2-digit' });
    } catch {
      return iso;
    }
  };
  const orderTypeLabel = (t) => ({ delivery: 'Bezorging', pickup: 'Afhalen', dine_in: 'Ter Plaatse' }[t] || t);

  return (
    <div className="min-h-screen bg-[#F8F9FA] flex items-center justify-center p-6">
      <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md w-full text-center" data-testid="order-success-page">

        {stage === 'checking_payment' && (
          <>
            <Loader2 className="w-16 h-16 text-[#1A4D2E] animate-spin mx-auto mb-4" />
            <h1 className="font-heading text-2xl font-bold text-gray-900 mb-2">Betaling Verwerken</h1>
            <p className="text-gray-500">Even geduld terwijl we uw betaling bevestigen...</p>
          </>
        )}

        {stage === 'awaiting_confirmation' && (
          <>
            <div className="relative w-20 h-20 mx-auto mb-4">
              <div className="absolute inset-0 rounded-full bg-amber-100"></div>
              <Clock className="w-10 h-10 text-amber-600 absolute inset-0 m-auto animate-pulse" />
            </div>
            <h1 className="font-heading text-2xl font-bold text-gray-900 mb-2" data-testid="awaiting-title">
              Bestelling Ontvangen
            </h1>
            <p className="text-gray-500 mb-2">
              Het restaurant gaat je bestelling nu bekijken.
            </p>
            <p className="text-gray-700 font-medium mb-6">
              Zodra de bestelling wordt geaccepteerd ontvang je hier <strong>de definitieve {order?.order_type === 'delivery' ? 'bezorg' : 'afhaal'}tijd</strong>.
            </p>

            {order && (
              <div className="bg-gray-50 rounded-xl p-4 mb-6 text-left text-sm">
                <div className="flex justify-between mb-1.5">
                  <span className="text-gray-500">Bestelnummer</span>
                  <span className="font-mono font-medium text-gray-900">{order.order_number}</span>
                </div>
                <div className="flex justify-between mb-1.5">
                  <span className="text-gray-500">Type</span>
                  <span className="font-medium text-gray-900">{orderTypeLabel(order.order_type)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Totaal</span>
                  <span className="font-medium text-gray-900">{formatPrice(order.total, order.currency)}</span>
                </div>
              </div>
            )}

            {paymentMethod === 'cash' && (
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6">
                <p className="text-sm text-amber-900">
                  Te betalen <strong>contant bij ontvangst</strong>
                </p>
              </div>
            )}

            <div className="flex items-center justify-center gap-2 text-sm text-gray-400 mb-6">
              <Loader2 className="w-4 h-4 animate-spin" />
              Wachten op bevestiging...
            </div>

            <Link to={webshopBack}>
              <Button variant="outline" data-testid="back-to-webshop-btn">Terug naar Webshop</Button>
            </Link>
          </>
        )}

        {stage === 'confirmed' && order && (
          <>
            <CheckCircle className="w-16 h-16 text-emerald-500 mx-auto mb-4" />
            <h1 className="font-heading text-2xl font-bold text-gray-900 mb-2" data-testid="confirmed-title">
              Bestelling Bevestigd!
            </h1>
            <p className="text-gray-500 mb-4">Het restaurant bereidt je bestelling voor.</p>

            {order.estimated_ready_time && (
              <div className="bg-gradient-to-br from-emerald-50 to-teal-50 border border-emerald-200 rounded-xl p-5 mb-6">
                <p className="text-xs uppercase tracking-wider text-emerald-700 font-medium mb-1">
                  {order.order_type === 'delivery' ? 'Verwachte bezorgtijd' : 'Klaar om af te halen om'}
                </p>
                <p className="font-heading text-4xl font-bold text-emerald-700" data-testid="ready-time">
                  {formatReadyTime(order.estimated_ready_time)}
                </p>
              </div>
            )}

            <div className="bg-gray-50 rounded-xl p-4 mb-6 text-left text-sm">
              <div className="flex justify-between mb-1.5">
                <span className="text-gray-500">Bestelnummer</span>
                <span className="font-mono font-medium text-gray-900">{order.order_number}</span>
              </div>
              <div className="flex justify-between mb-1.5">
                <span className="text-gray-500">Type</span>
                <span className="font-medium text-gray-900">{orderTypeLabel(order.order_type)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Totaal</span>
                <span className="font-medium text-gray-900">{formatPrice(order.total, order.currency)}</span>
              </div>
            </div>

            {paymentMethod === 'cash' && (
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6">
                <p className="text-sm text-amber-900">
                  Te betalen <strong>contant bij ontvangst</strong>
                </p>
              </div>
            )}

            <Link to={webshopBack}>
              <Button className="btn-primary" data-testid="back-to-webshop-btn">Terug naar Webshop</Button>
            </Link>
          </>
        )}

        {stage === 'rejected' && (
          <>
            <X className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h1 className="font-heading text-2xl font-bold text-gray-900 mb-2">Bestelling Geweigerd</h1>
            <p className="text-gray-500 mb-6">
              Helaas kon het restaurant je bestelling niet aanvaarden.
              {paymentMethod !== 'cash' && ' Je betaling wordt automatisch teruggestort.'}
            </p>
            <Link to={webshopBack}>
              <Button className="btn-primary" data-testid="back-to-webshop-btn">Terug naar Webshop</Button>
            </Link>
          </>
        )}

        {stage === 'payment_pending' && (
          <>
            <Clock className="w-16 h-16 text-yellow-500 mx-auto mb-4" />
            <h1 className="font-heading text-2xl font-bold text-gray-900 mb-2">Betaling In Behandeling</h1>
            <p className="text-gray-500 mb-6">Uw betaling wordt nog verwerkt. Controleer later opnieuw.</p>
            <Link to={webshopBack}>
              <Button className="btn-primary" data-testid="back-to-webshop-btn">Terug naar Webshop</Button>
            </Link>
          </>
        )}

        {stage === 'payment_failed' && (
          <>
            <X className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h1 className="font-heading text-2xl font-bold text-gray-900 mb-2" data-testid="payment-failed-title">Betaling Mislukt</h1>
            <p className="text-gray-500 mb-6">Uw betaling is niet gelukt of geannuleerd. Er is niets in rekening gebracht. Probeer het opnieuw.</p>
            <Link to={webshopBack}>
              <Button className="btn-primary" data-testid="back-to-webshop-btn">Terug naar Webshop</Button>
            </Link>
          </>
        )}

        {stage === 'payment_error' && (
          <>
            <X className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h1 className="font-heading text-2xl font-bold text-gray-900 mb-2">Er Ging Iets Mis</h1>
            <p className="text-gray-500 mb-6">We konden uw betaling niet verifiëren. Neem contact op met support.</p>
            <Link to={webshopBack}>
              <Button className="btn-primary" data-testid="back-to-webshop-btn">Terug naar Webshop</Button>
            </Link>
          </>
        )}
      </div>
    </div>
  );
};
